import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, Sparkles, Save, FileDown, Loader2, Plus, Trash2,
  FileText, Maximize2, Info,
} from "lucide-react";
import api, { API, formatApiError, getToken } from "@/api";
import Header from "@/components/Header";

const CATEGORIES = [
  {
    key: "pipe",
    title: "Pipe",
    addLabel: "Add pipe",
    cols: [
      { k: "size", label: "Size", w: "w-24" },
      { k: "material", label: "Material", w: "w-56" },
      { k: "length_ft", label: "Length (ft)", w: "w-28", num: true },
      { k: "notes", label: "Notes", w: "" },
    ],
    empty: { size: "", material: "", length_ft: 0, notes: "" },
  },
  {
    key: "fittings",
    title: "Fittings",
    addLabel: "Add fitting",
    cols: [
      { k: "type", label: "Type", w: "w-40" },
      { k: "size", label: "Size", w: "w-24" },
      { k: "material", label: "Material", w: "w-40" },
      { k: "quantity", label: "Qty", w: "w-20", num: true },
      { k: "notes", label: "Notes", w: "" },
    ],
    empty: { type: "", size: "", material: "", quantity: 0, notes: "" },
  },
  {
    key: "heads",
    title: "Sprinkler Heads",
    addLabel: "Add head",
    cols: [
      { k: "type", label: "Type", w: "w-36" },
      { k: "temp_rating", label: "Temp", w: "w-24" },
      { k: "finish", label: "Finish", w: "w-28" },
      { k: "k_factor", label: "K", w: "w-16" },
      { k: "quantity", label: "Qty", w: "w-20", num: true },
      { k: "notes", label: "Notes", w: "" },
    ],
    empty: { type: "", temp_rating: "", finish: "", k_factor: "", quantity: 0, notes: "" },
  },
  {
    key: "hangers",
    title: "Hangers / Supports",
    addLabel: "Add hanger",
    cols: [
      { k: "type", label: "Type", w: "w-56" },
      { k: "size", label: "Size", w: "w-24" },
      { k: "quantity", label: "Qty", w: "w-20", num: true },
      { k: "notes", label: "Notes", w: "" },
    ],
    empty: { type: "", size: "", quantity: 0, notes: "" },
  },
];

const emptyTakeoff = () => ({
  drawing_summary: "",
  confidence: "",
  detected_scale: "",
  pipe: [],
  fittings: [],
  valves: [],
  heads: [],
  hangers: [],
});

export default function Editor() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();

  const [project, setProject] = useState(null);
  const [takeoff, setTakeoff] = useState(emptyTakeoff());
  const [scale, setScale] = useState("");
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadProject = useCallback(() => {
    api
      .get(`/projects/${pid}`)
      .then((r) => {
        setProject(r.data);
        setScale(r.data.scale || "");
        if (r.data.takeoff) setTakeoff({ ...emptyTakeoff(), ...r.data.takeoff });
      })
      .catch(() => navigate("/"));
  }, [pid, navigate]);

  useEffect(loadProject, [loadProject]);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2500);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${pid}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProject(data);
      flash("Drawing uploaded. Run the AI takeoff next.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const analyze = async () => {
    setError("");
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/projects/${pid}/analyze`);
      setProject(data);
      if (data.takeoff) setTakeoff({ ...emptyTakeoff(), ...data.takeoff });
      if (data.scale) setScale(data.scale);
      flash("AI takeoff drafted. Review and adjust the quantities.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setAnalyzing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put(`/projects/${pid}/takeoff`, { scale, takeoff });
      setProject(data);
      flash("Takeoff saved.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    setError("");
    try {
      // Persist the latest on-screen edits first so the export always matches the table.
      await api.put(`/projects/${pid}/takeoff`, { scale, takeoff });
      const res = await api.get(`/projects/${pid}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project?.name || "takeoff").replace(/[^a-z0-9_\- ]/gi, "")}_takeoff.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = err.message || "Export failed.";
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          try {
            msg = formatApiError(JSON.parse(text).detail) || msg;
          } catch (_) {
            msg = text || msg;
          }
        } else if (err.response?.data?.detail) {
          msg = formatApiError(err.response.data.detail);
        }
      } catch (_) {}
      const status = err.response?.status ? ` (status ${err.response.status})` : " (network/connection)";
      setError(`Export failed: ${msg}${status}`);
    } finally {
      setExporting(false);
    }
  };

  const updateCell = (cat, idx, key, value) => {
    setTakeoff((t) => {
      const arr = [...(t[cat] || [])];
      arr[idx] = { ...arr[idx], [key]: value };
      return { ...t, [cat]: arr };
    });
  };
  const addRow = (cat, empty) =>
    setTakeoff((t) => ({ ...t, [cat]: [...(t[cat] || []), { ...empty }] }));
  const delRow = (cat, idx) =>
    setTakeoff((t) => ({ ...t, [cat]: t[cat].filter((_, i) => i !== idx) }));

  if (!project) {
    return (
      <div className="App">
        <Header />
        <div className="grid place-items-center h-[60vh] text-slate-400">
          <Loader2 className="animate-spin" />
        </div>
      </div>
    );
  }

  const hasDrawing = !!project.drawing;
  const hasTakeoff = project.status === "analyzed";
  const drawingUrl = hasDrawing
    ? `${API}/projects/${pid}/drawing?auth=${encodeURIComponent(getToken() || "")}`
    : null;
  const isPdf = project.drawing?.ext === "pdf";

  return (
    <div className="App flex flex-col h-screen overflow-hidden">
      <Header />

      {/* Sub toolbar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex flex-wrap items-center gap-3">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={16} /> Projects
        </button>
        <div className="h-5 w-px bg-slate-200" />
        <h1 className="font-display font-bold tracking-tight text-lg text-slate-900 truncate max-w-[40vw]">
          {project.name}
        </h1>
        <div className="flex-1" />
        <button
          data-testid="save-takeoff-btn"
          onClick={save}
          disabled={saving || !hasTakeoff}
          className="flex items-center gap-2 border-2 border-slate-900 text-slate-900 font-semibold px-4 py-2 hover:bg-slate-50 transition-colors disabled:opacity-40"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
        <button
          data-testid="export-excel-btn"
          onClick={exportExcel}
          disabled={exporting || !hasTakeoff}
          className="flex items-center gap-2 bg-[#16A34A] text-white font-semibold px-4 py-2 hover:bg-green-700 transition-colors disabled:opacity-40"
        >
          {exporting ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />} Export to Excel
        </button>
      </div>

      {(error || notice) && (
        <div
          className={`px-6 py-2 text-sm ${error ? "bg-red-50 text-[#DC2626] border-b border-red-200" : "bg-green-50 text-green-700 border-b border-green-200"}`}
          data-testid="editor-banner"
        >
          {error || notice}
        </div>
      )}

      {/* Split pane */}
      <div className="flex-1 grid lg:grid-cols-[42%_58%] overflow-hidden">
        {/* LEFT: Drawing */}
        <div className="border-r border-slate-200 bg-slate-100 flex flex-col overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span className="uppercase text-xs font-bold tracking-[0.2em] text-slate-500">Drawing</span>
            {hasDrawing && (
              <a
                data-testid="open-drawing-link"
                href={drawingUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"
              >
                <Maximize2 size={13} /> Full size
              </a>
            )}
          </div>

          <div className="flex-1 overflow-auto thin-scroll p-4">
            {!hasDrawing ? (
              <div
                data-testid="upload-zone"
                onClick={() => fileRef.current?.click()}
                className="h-full min-h-[300px] border-2 border-dashed border-slate-300 bg-white hover:bg-slate-50 transition-all flex flex-col items-center justify-center cursor-pointer text-center p-8"
              >
                {uploading ? (
                  <Loader2 size={34} className="animate-spin text-[#002FA7]" />
                ) : (
                  <Upload size={34} className="text-slate-400" />
                )}
                <h3 className="font-display font-bold text-lg text-slate-900 mt-4">
                  {uploading ? "Uploading…" : "Upload sprinkler drawing"}
                </h3>
                <p className="text-sm text-slate-500 mt-1">PDF, PNG, JPG or WEBP · up to 25MB</p>
              </div>
            ) : isPdf ? (
              <iframe
                data-testid="drawing-pdf"
                title="drawing"
                src={drawingUrl}
                className="w-full h-full min-h-[70vh] bg-white border border-slate-200"
              />
            ) : (
              <img
                data-testid="drawing-image"
                src={drawingUrl}
                alt="Sprinkler drawing"
                className="w-full bg-white border border-slate-200"
              />
            )}
            <input
              ref={fileRef}
              data-testid="file-input"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={onUpload}
            />
          </div>

          {hasDrawing && (
            <div className="p-4 border-t border-slate-200 bg-white space-y-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <FileText size={14} />
                <span className="truncate">{project.drawing.original_filename}</span>
                <button
                  data-testid="replace-drawing-btn"
                  onClick={() => fileRef.current?.click()}
                  className="ml-auto font-semibold text-[#002FA7] hover:underline"
                >
                  Replace
                </button>
              </div>
              <label className="block">
                <span className="uppercase text-[11px] font-bold tracking-[0.15em] text-slate-500">
                  Drawing scale (optional)
                </span>
                <input
                  data-testid="scale-input"
                  value={scale}
                  onChange={(e) => setScale(e.target.value)}
                  placeholder={`e.g. 1/8" = 1'-0"`}
                  className="mt-1 w-full border border-slate-300 px-3 py-2 rounded-sm text-sm outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
                />
              </label>
              <button
                data-testid="analyze-btn"
                onClick={analyze}
                disabled={analyzing}
                className="w-full flex items-center justify-center gap-2 bg-[#002FA7] text-white font-semibold px-4 py-3 hover:bg-[#002080] transition-colors disabled:opacity-60"
              >
                {analyzing ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                {analyzing ? "Analyzing drawing…" : hasTakeoff ? "Re-run AI takeoff" : "Generate AI takeoff"}
              </button>
            </div>
          )}
        </div>

        {/* RIGHT: Takeoff table */}
        <div className="overflow-auto thin-scroll bg-white">
          {!hasTakeoff && !analyzing ? (
            <div className="h-full grid place-items-center p-10 text-center">
              <div className="max-w-sm">
                <span className="inline-grid place-items-center w-14 h-14 bg-slate-100 text-[#002FA7] mb-4">
                  <Sparkles size={26} />
                </span>
                <h3 className="font-display text-2xl font-black tracking-tight text-slate-900">
                  No takeoff yet
                </h3>
                <p className="text-slate-600 mt-2">
                  {hasDrawing
                    ? "Hit “Generate AI takeoff” to draft pipe, fittings, heads and hangers from your drawing."
                    : "Upload a drawing on the left to get started."}
                </p>
              </div>
            </div>
          ) : analyzing ? (
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 text-slate-600">
                <Loader2 className="animate-spin text-[#002FA7]" />
                <span className="font-semibold">AI is reading your drawing…</span>
              </div>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-10 bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="p-5 space-y-8">
              {(takeoff.drawing_summary || takeoff.confidence) && (
                <div className="bg-blue-50 border border-blue-200 p-4 flex gap-3" data-testid="ai-summary">
                  <Info size={18} className="text-[#2563EB] shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-700">
                    {takeoff.drawing_summary}
                    {takeoff.confidence && (
                      <span className="ml-2 uppercase text-[11px] font-bold tracking-wider text-slate-500">
                        · AI confidence: {takeoff.confidence}
                      </span>
                    )}
                    <div className="mt-1 text-xs text-slate-500">
                      AI estimates are a starting draft — verify quantities before bidding.
                    </div>
                  </div>
                </div>
              )}

              {CATEGORIES.map((cat) => (
                <CategoryTable
                  key={cat.key}
                  cat={cat}
                  rows={takeoff[cat.key] || []}
                  onCell={(idx, k, v) => updateCell(cat.key, idx, k, v)}
                  onAdd={() => addRow(cat.key, cat.empty)}
                  onDelete={(idx) => delRow(cat.key, idx)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryTable({ cat, rows, onCell, onAdd, onDelete }) {
  return (
    <section data-testid={`category-${cat.key}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-bold text-lg tracking-tight text-slate-900">
          {cat.title}{" "}
          <span className="text-slate-400 font-sans font-medium text-sm">({rows.length})</span>
        </h3>
        <button
          data-testid={`add-${cat.key}-btn`}
          onClick={onAdd}
          className="flex items-center gap-1 text-sm font-semibold text-[#002FA7] hover:underline"
        >
          <Plus size={15} /> {cat.addLabel}
        </button>
      </div>
      <div className="overflow-x-auto thin-scroll border border-slate-200">
        <table className="w-full text-left text-sm text-slate-700">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-900 uppercase tracking-wider text-[11px]">
              {cat.cols.map((c) => (
                <th key={c.k} className={`px-3 py-2 font-bold ${c.w}`}>{c.label}</th>
              ))}
              <th className="px-2 py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={cat.cols.length + 1} className="px-3 py-4 text-slate-400 text-center">
                  No {cat.title.toLowerCase()} — click “{cat.addLabel}”.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                  {cat.cols.map((c) => (
                    <td key={c.k} className="px-2 py-1">
                      <input
                        data-testid={`${cat.key}-${idx}-${c.k}`}
                        type={c.num ? "number" : "text"}
                        value={row[c.k] ?? ""}
                        onChange={(e) =>
                          onCell(idx, c.k, c.num ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)
                        }
                        className="w-full bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#002FA7] px-1 py-1 outline-none"
                      />
                    </td>
                  ))}
                  <td className="px-2 py-1 text-center">
                    <button
                      data-testid={`delete-${cat.key}-${idx}`}
                      onClick={() => onDelete(idx)}
                      className="text-slate-300 hover:text-[#DC2626] transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
